import React, { useState, useEffect } from "react";
import hosys from "@/api/api";
import { useVenue } from "@/components/shared/VenueContext";
import { PageGuard } from "@/components/shared/PlanGuard";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  ShieldCheck, Flame, Gauge, ChefHat, Calendar, FileText, RefreshCw, LayoutDashboard,
} from "lucide-react";
import FireProtectionTab from "@/components/technical/FireProtectionTab";
import GasInstallationTab from "@/components/technical/GasInstallationTab";
import KitchenEquipmentTab from "@/components/technical/KitchenEquipmentTab";
import TechnicalCalendar from "@/components/technical/TechnicalCalendar";
import TechnicalDashboard from "@/components/technical/TechnicalDashboard";
import TechnicalDossier from "@/components/technical/TechnicalDossier";

export default function TechnicalCompliance() {
  const { currentVenue } = useVenue();
  const [fire, setFire] = useState([]);
  const [gas, setGas] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [electrical, setElectrical] = useState([]);
  const [maintenances, setMaintenances] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!currentVenue) return;
    setLoading(true);
    const [f, g, eq, el, maint] = await Promise.all([
      hosys.entities.FireProtection.filter({ venue_id: currentVenue.id, active: true }),
      hosys.entities.GasInstallation.filter({ venue_id: currentVenue.id, active: true }),
      hosys.entities.KitchenEquipment.filter({ venue_id: currentVenue.id, active: true }),
      hosys.entities.ElectricalInstallation.filter({ venue_id: currentVenue.id, active: true }),
      hosys.entities.TechnicalMaintenance.filter({ venue_id: currentVenue.id }, "-maintenance_date", 100),
    ]);
    setFire(f);
    setGas(g);
    setEquipment(eq);
    setElectrical(el);
    setMaintenances(maint);
    setLoading(false);
  };

  useEffect(() => { load(); }, [currentVenue?.id]);

  const openAlerts = [
    ...fire.filter(e => e.status === "expired" || e.status === "deficient"),
    ...gas.filter(e => e.status === "expired" || e.status === "incident_open"),
    ...equipment.filter(e => e.status === "avariat"),
  ].length;

  return (
    <PageGuard moduleCode="technical_compliance">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#7B2D3F]/10 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-[#7B2D3F]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Seguretat Tècnica del Local</h1>
              <p className="text-xs text-gray-500">Incendis · Gas · Equips cuina · Elèctrica</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} className="gap-1">
            <RefreshCw className="w-3.5 h-3.5" />
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-400">Carregant...</div>
        ) : (
          <Tabs defaultValue="dashboard">
            <div className="overflow-x-auto -mx-1 px-1">
              <TabsList className="mb-4 flex w-max min-w-full">
                <TabsTrigger value="dashboard" className="gap-1.5 text-xs sm:text-sm">
                  <LayoutDashboard className="w-4 h-4" />
                  <span className="hidden sm:inline">Panell</span>
                </TabsTrigger>
                <TabsTrigger value="fire" className="gap-1.5 text-xs sm:text-sm">
                  <Flame className="w-4 h-4" />
                  <span className="hidden sm:inline">Incendis</span>
                  <span className="sm:hidden">Foc</span>
                  {fire.length > 0 && <span className="ml-0.5 text-[10px] text-gray-400">({fire.length})</span>}
                </TabsTrigger>
                <TabsTrigger value="gas" className="gap-1.5 text-xs sm:text-sm">
                  <Gauge className="w-4 h-4" />
                  Gas
                  {gas.length > 0 && <span className="ml-0.5 text-[10px] text-gray-400">({gas.length})</span>}
                </TabsTrigger>
                <TabsTrigger value="equipment" className="gap-1.5 text-xs sm:text-sm">
                  <ChefHat className="w-4 h-4" />
                  <span className="hidden sm:inline">Equips</span>
                  {equipment.length > 0 && <span className="ml-0.5 text-[10px] text-gray-400">({equipment.length})</span>}
                </TabsTrigger>
                <TabsTrigger value="calendar" className="gap-1.5 text-xs sm:text-sm">
                  <Calendar className="w-4 h-4" />
                  <span className="hidden sm:inline">Calendari</span>
                </TabsTrigger>
                <TabsTrigger value="dossier" className="gap-1.5 text-xs sm:text-sm">
                  <FileText className="w-4 h-4" />
                  Dossier
                </TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="dashboard">
              <TechnicalDashboard
                fire={fire}
                gas={gas}
                equipment={equipment}
                electrical={electrical}
              />
            </TabsContent>

            <TabsContent value="fire">
              <FireProtectionTab
                items={fire}
                onRefresh={load}
                venueId={currentVenue?.id}
              />
            </TabsContent>

            <TabsContent value="gas">
              <GasInstallationTab
                items={gas}
                onRefresh={load}
                venueId={currentVenue?.id}
              />
            </TabsContent>

            <TabsContent value="equipment">
              <KitchenEquipmentTab
                items={equipment}
                maintenances={maintenances.filter(m => m.asset_type === "kitchen_equipment")}
                onRefresh={load}
                venueId={currentVenue?.id}
              />
            </TabsContent>

            <TabsContent value="calendar">
              <TechnicalCalendar
                fire={fire}
                gas={gas}
                equipment={equipment}
                electrical={electrical}
              />
            </TabsContent>

            <TabsContent value="dossier">
              <TechnicalDossier
                fire={fire}
                gas={gas}
                equipment={equipment}
                electrical={electrical}
                maintenances={maintenances}
              />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </PageGuard>
  );
}